import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import axios, { AxiosError } from 'axios';
import { EMPTY, timer } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { getEncryptor } from '../utils/encryption/encryption';
import { isDefined } from '../utils/utils';
import { EventBusService } from './eventBus.service';
import { EVENTS } from '../utils/constants';
import { HTTP_MESSAGES } from '../utils/messages';
import { Router } from '@angular/router';

type RequestMethod = 'get' | 'post' | 'delete' | 'put' | 'patch';

@Injectable({
  providedIn: 'root'
})
export class CoreService {
  constructor(
    private http: HttpClient,
    private matSnackBar: MatSnackBar,
    public eventBusService: EventBusService,
    private router: Router
  ) {}

  // Sesión sin renovación: el token del login vale 24 h y no hay refresh. Cuando expira,
  // cualquier 401 cierra la sesión y devuelve al login (ver handleError). Un flag evita
  // repetirlo N veces cuando varias peticiones en vuelo fallan a la vez.
  private sessionExpiredEmitted = false;

  async request<T>(
    method: RequestMethod,
    url: string,
    options?: any,
    retriesCount: number = 3
  ): Promise<{
    data?: T;
    status: number;
    error?: any;
    message?: string;
    errorShowed?: boolean;
  }> {
    if (environment.enableEncryption) {
      const { encrypt, decrypt, a2V5 } = getEncryptor();
      let paramsStr = '';
      try {
        const encryptedPath = encrypt(url);

        // El sobre cifrado se arma en variables locales, sin tocar `options`. Mutarlo
        // rompía cualquier segundo envío de la misma petición: se cifraba un body ya
        // cifrado, el gateway descifraba una sola capa y el ValidationPipe recibía
        // `{ ZGF0YQ: '<hex>' }` → 400 "property ZGF0YQ should not exist" (así se
        // manifestaba en el login cuando existía el reintento por refresh).
        const headers = {
          ...(options.headers ?? {}),
          a2V5: a2V5 as any
        };

        let bodyToSend = options.body;
        if (isDefined(options.body) && !(options.body instanceof FormData)) {
          bodyToSend = { ZGF0YQ: encrypt(JSON.stringify(options.body)) };
        }
        if (isDefined(options.params)) {
          const encryptedParams = encrypt(JSON.stringify(options.params));
          paramsStr = `&TYU8Q=${encryptedParams}`;
        }

        const uri = `${environment.api_url}/?ZGF0YQ=${encryptedPath}${paramsStr}`;

        const res = await axios.request<T>({
          method,
          url: uri,
          // Migración JWT→cookie: envía la cookie httpOnly de sesión solo en modo cookie.
          // Con useCookieAuth=false (modo header) NO se fuerzan credenciales → evita
          // cambiar el comportamiento CORS y mandar cookies innecesarias.
          withCredentials: environment.useCookieAuth,
          data: bodyToSend ?? {},
          headers,
          responseType: options.responseType || 'json',
          transformResponse:
            options.responseType === 'blob'
              ? undefined
              : (data) => {
                  if (!data) return {};

                  try {
                    const decData = decrypt(data);
                    const decoded = decData ? JSON.parse(decData) : null;

                    return decoded;
                  } catch (err) {
                    return { encryptionError: true };
                  }
                }
        });

        return { data: res.data, status: res.status };
      } catch (error) {
        return this.handleError(error as AxiosError, method, url, options, 0);
      }
    } else {
      try {
        const res = await axios.request<T>({
          method,
          url: `${environment.api_url}/${url}`,
          withCredentials: environment.useCookieAuth,
          data: options.body,
          headers: options.headers,
          // `options.params` se serializa como query string (`?k=v&...`).
          // Sin esto los callers que pasan `{ params: { before, beforeId, ... } }`
          // veían la request salir pelada → el server respondía el default y
          // el caller no podía paginar (ej. loadMore del support-chat).
          params: options.params,
          responseType: options.responseType || 'json'
        });

        return { data: res.data, status: res.status };
      } catch (error) {
        return this.handleError(error as AxiosError, method, url, options, 0);
      }
    }
  }

  private handleError(error: AxiosError, method, url, options, retriesCount: number) {
    let errorShowed = false;
    let redirected = false;
    let resData = error.response?.data as any;
    let code = error.code;
    // Cuando el caller pide silentError, no mostramos el snackbar global de error
    // (el caller maneja su propio mensaje). Redirects/logout se mantienen.
    const silent = options?.silentError === true;
    // `background: true` es para peticiones que el usuario no ha pedido (p. ej. el latido
    // de presencia): además de callar, no deben cerrar la sesión ni navegar a otra ruta.
    // Un fallo suyo se limita a devolver el error al caller; de reaccionar al 401 ya se
    // encarga la siguiente petición real del usuario.
    const background = options?.background === true;

    if (!['production', 'prod'].includes(environment.env.toLowerCase())) {
      console.log(error, 'error');
      console.log(error, 'error in handleError');
      console.log(resData, 'resData');
    }

    // El ValidationPipe de Nest devuelve `message` como array de strings. Sin esto,
    // el snackbar mostraba "msg1,msg2" pegado. Lo normalizamos a una frase legible.
    if (Array.isArray(resData?.message)) {
      resData.message = resData.message.join('. ');
    }

    // El backend usa 401 para DOS cosas distintas: no estar autenticado (AuthGuard) y no tener
    // permiso (RbacGuard → «User not authorized to: <permiso>», que semánticamente sería un
    // 403). Solo la primera es una sesión caída; tratar la segunda como tal echaría al usuario
    // al login por un permiso que le falta, y al volver a entrar le pasaría lo mismo.
    const rawMessage = Array.isArray(resData?.message)
      ? resData.message.join('. ')
      : String(resData?.message ?? '');
    const isPermissionDenied = /not authorized to/i.test(rawMessage);

    // Sesión expirada (o token revocado): sin refresh, un 401 así significa volver al login.
    // Se excluyen los endpoints donde un 401 es una respuesta legítima y NO una sesión
    // caída: el propio login (credenciales incorrectas), el logout (el token ya no vale) y
    // las comprobaciones de sesión del arranque, que corren precisamente sin sesión.
    const isAuthPublicCall = /auth\/(login|logout|me|session|register|forgot-password|validate)/.test(
      url
    );
    if (error.response?.status === 401 && isPermissionDenied) {
      if (!silent)
        this.matSnackBar.open('No tiene permiso para realizar esta acción.', 'Ok', {
          duration: 4000,
          panelClass: 'mat-snack-error'
        });
      return {
        data: null,
        error: { ...(resData || {}), code: 'PERMISSION_DENIED' },
        message: rawMessage,
        status: 401,
        errorShowed: true
      };
    }

    if (error.response?.status === 401 && !isAuthPublicCall) {
      if (!background && !this.sessionExpiredEmitted) {
        this.sessionExpiredEmitted = true;
        setTimeout(() => (this.sessionExpiredEmitted = false), 10000);
        if (!silent)
          this.matSnackBar.open('Su sesión expiró. Por favor, inicie sesión de nuevo.', 'Ok', {
            duration: 4000,
            panelClass: 'mat-snack-error'
          });
        this.eventBusService.emit(EVENTS.logout_user);
      }
      return {
        data: null,
        error: { redirected: !background },
        message: 'Su sesión expiró. Por favor, inicie sesión de nuevo.',
        status: 401,
        errorShowed: true
      };
    }

    // 504 del proxy: el backend no contestó a tiempo, pero puede haber terminado la
    // operación después. Se mira el código y no solo `statusText`, que por HTTP/2 llega
    // vacío; sin esto el usuario veía el texto crudo de axios («Request failed with
    // status code 504»). Se devuelve aquí para que el componente pinte el mismo aviso.
    if (error.response?.status === 504 || error.response?.statusText === 'Gateway Timeout') {
      if (!silent)
        this.matSnackBar.open(HTTP_MESSAGES['GATEWAY_TIMEOUT'], 'Ok', {
          duration: 7000,
          panelClass: 'mat-snack-error'
        });
      return {
        data: null,
        error: { code: 'GATEWAY_TIMEOUT', redirected: false },
        message: HTTP_MESSAGES['GATEWAY_TIMEOUT'],
        status: 504,
        statusText: error.response?.statusText,
        errorShowed: true
      };
    }

    if (error.code === 'ERR_NETWORK') {
      errorShowed = true;
      if (!silent)
        this.matSnackBar.open(HTTP_MESSAGES['ERR_NETWORK'], 'Ok', {
          duration: 4000,
          panelClass: 'mat-snack-error'
        });
    } else if (error.code === 'ECONNREFUSED') {
      errorShowed = true;
      if (!silent)
        this.matSnackBar.open(HTTP_MESSAGES['ECONNREFUSED'], 'Ok', {
          duration: 4000,
          panelClass: 'mat-snack-error'
        });
    } else if (!resData) {
      if (!background) {
        this.router.navigateByUrl('/error');
        redirected = true;
      }
    } else if (resData && typeof resData === 'string') {
      resData = JSON.parse(resData);
      code = resData?.message?.code ?? resData?.status;
    } else if (resData?.statusCode === 403) {
      if (!background) {
        this.router.navigateByUrl('/404');
        // sessionStorage.setItem(`${method} to ${url}_error`, JSON.stringify(error.toJSON?.()));

        redirected = true;
      }
    } else if (resData?.message == 'Su sesión expiró. Por favor, inicie sesión de nuevo.') {
      if (!background) this.eventBusService.emit(EVENTS.logout_user);
    } else if (error.response?.status === 412 && resData?.code === 'KYC_REQUIRED') {
      // Fallback si el gate local no aplicó (config aún no refrescada): el
      // backend bloqueó la extracción por KYC → informar y llevar a la
      // verificación.
      if (!background) {
        errorShowed = true;
        this.matSnackBar.open(
          resData.message ||
            'Debes completar la verificación de identidad (KYC) para realizar esta operación.',
          'Entendido',
          {
            duration: 6000,
            horizontalPosition: 'center',
            verticalPosition: 'top'
          }
        );
        this.router.navigate(['/settings/kyc']);
        redirected = true;
      }
    } else if (resData?.message) {
      errorShowed = true;
      if (!silent)
        this.matSnackBar.open(resData.message, 'Ok', {
          duration: 4000,
          panelClass: 'mat-snack-error'
        });
    } else if (HTTP_MESSAGES[code]) {
      errorShowed = true;
      if (!silent)
        this.matSnackBar.open(HTTP_MESSAGES[code], 'Ok', {
          duration: 4000,
          panelClass: 'mat-snack-error'
        });
    }
    const resError = resData?.error ?? {};
    delete resData?.error;
    return {
      data: null,
      error: {
        ...resError,
        ...(resData || {}),
        code: code ?? resData?.errors?.code,
        redirected
      },
      // El mensaje del backend va PRIMERO: `error.message` de axios siempre viene
      // relleno ("Request failed with status code 413"), así que anteponerlo dejaba
      // `resData.message` muerto y los componentes pintaban el texto crudo de la
      // librería en lugar de la explicación en español que manda el gateway.
      message: resData?.message ?? error?.message,
      status: error.status ?? error.response?.status,
      statusText: error.response?.statusText,
      errorShowed
    };
  }

  private handleRetry(method, url, options, retriesLeft: number) {
    const delay = retriesLeft == 3 ? 5000 : 10000;

    return timer(delay).pipe(
      // mergeMap(() => this.request(method, url, options, retriesLeft)),
      catchError(() => EMPTY)
    );
  }

  private shouldRetry(error: HttpErrorResponse, retriesCount: number): boolean {
    return false;
    // if (retriesCount < 1) return false
    // return error.status === 0 || (error.status >= 500 && error.status < 600)
  }

  get(url: string, options: any) {
    return this.request('get', url, options);
  }
  post(url: string, options: any) {
    return this.request('post', url, options);
  }
  put(url: string, options: any) {
    return this.request('put', url, options);
  }
  patch(url: string, options: any) {
    return this.request('patch', url, options);
  }
  delete(url: string, options: any) {
    return this.request('delete', url, options);
  }
}

const v = {
  payFromCountry: 'Canadá',
  payFromBank: 'EpCards Treew',
  payFromAccount: 'Payments Treew',
  payFromHolder: 'VISA EpCards Treew'
};

const t = {
  payFromCountry: 'España',
  payFromBank: 'Tropipay Treew',
  payFromAccount: 'payments@treew.com',
  payFromHolder: 'Tropipay Treew'
};
